/**
 * Firestore Listeners Service
 *
 * This service manages Firestore real-time listeners outside of Redux to avoid serialization issues.
 * It provides a clean interface for registering, unregistering, and cleaning up listeners.
 *
 * Note: Cursor and typing listeners are envisioned, but not supported yet. If/when they do
 * become supported, this will likely be via a separate service. This is more appropriately handled
 * via web sockets writing/reading to Firestore.
 */

import { parseListenerId, type ListenerType } from '../utils/listenerIds';
import { debugLog, debugWarn } from '../../utils/debug';

// Map to store active listeners by ID
const activeListeners = new Map<string, () => void>();

/**
 * Register a new Firestore listener
 * @param id - Unique identifier for the listener
 * @param unsubscribe - Function returned by onSnapshot to unsubscribe
 */
export const registerListener = (id: string, unsubscribe: () => void): void => {
  // If listener already exists, clean it up first
  if (activeListeners.has(id)) {
    debugWarn(`[FirestoreListeners] Replacing existing listener: ${id}`);
    const existingUnsubscribe = activeListeners.get(id);
    if (existingUnsubscribe) {
      try {
        existingUnsubscribe();
      } catch (error) {
        debugWarn(`[FirestoreListeners] Error cleaning up existing listener ${id}:`, error);
      }
    }
  }

  activeListeners.set(id, unsubscribe);

  debugLog(`[FirestoreListeners] Registered listener: ${id} (total: ${activeListeners.size})`);
};

/**
 * Unregister and cleanup a specific Firestore listener
 * @param id - Unique identifier for the listener
 * @returns True if listener was found and cleaned up, false otherwise
 */
export const unregisterListener = (id: string): boolean => {
  const unsubscribe = activeListeners.get(id);
  if (unsubscribe) {
    try {
      unsubscribe();
      activeListeners.delete(id);
      debugLog(
        `[FirestoreListeners] Unregistered listener: ${id} (remaining: ${activeListeners.size})`,
      );
      return true;
    } catch (error) {
      console.warn(`[FirestoreListeners] Error unsubscribing listener ${id}:`, error);
      // Remove from map even if unsubscribe failed to prevent memory leaks
      activeListeners.delete(id);
      return false;
    }
  } else {
    debugWarn(`[FirestoreListeners] Attempted to unregister non-existent listener: ${id}`);
    return false;
  }
};

/**
 * Clean up all registered Firestore listeners
 * Useful for app shutdown or emergency cleanup scenarios
 */
export const cleanupAllListeners = () => {
  const listenerCount = activeListeners.size;
  debugLog(`[FirestoreListeners] Cleaning up ${listenerCount} listeners`);

  let successCount = 0;
  let errorCount = 0;

  activeListeners.forEach((unsubscribe, id) => {
    try {
      unsubscribe();
      debugLog(`[FirestoreListeners] Cleaned up listener: ${id}`);
      successCount++;
    } catch (error) {
      console.warn(`[FirestoreListeners] Error cleaning up listener ${id}:`, error);
      errorCount++;
    }
  });

  activeListeners.clear();

  if (errorCount > 0) {
    console.warn(
      `[FirestoreListeners] Cleanup completed with ${errorCount} errors out of ${listenerCount} listeners`,
    );
  } else {
    debugLog(`[FirestoreListeners] Successfully cleaned up all ${successCount} listeners`);
  }
};

/**
 * Clean up listeners by type
 *
 * @param listenerType - Type of listeners to cleanup ('milestoneMap', 'objectives', 'presence', etc.)
 * @returns Number of listeners cleaned up
 */
export function cleanupListenersByType(listenerType: ListenerType): number {
  debugLog(`[FirestoreListeners] Cleaning up all '${listenerType}' listeners`);

  let cleanedCount = 0;
  const listenersToCleanup: Array<{ id: string; unsubscribe: () => void }> = [];

  // Find all listeners of the specified type
  activeListeners.forEach((unsubscribe, id) => {
    const parsed = parseListenerId(id);
    if (parsed && parsed.type === listenerType) {
      listenersToCleanup.push({ id, unsubscribe });
    }
  });

  // Clean up the found listeners
  listenersToCleanup.forEach(({ id, unsubscribe }) => {
    try {
      unsubscribe();
      activeListeners.delete(id);
      debugLog(`[FirestoreListeners] Cleaned up ${listenerType} listener: ${id}`);
      cleanedCount++;
    } catch (error) {
      console.warn(`[FirestoreListeners] Error cleaning up ${listenerType} listener ${id}:`, error);
      // Remove from map even if unsubscribe failed
      activeListeners.delete(id);
    }
  });

  debugLog(
    `[FirestoreListeners] Cleaned up ${cleanedCount} '${listenerType}' listeners (remaining: ${activeListeners.size})`,
  );
  return cleanedCount;
}

/**
 * Get listener count by type for debugging/verification
 * @param listenerType - Type of listeners to count (optional)
 * @returns Count for specific type, or object with counts by type
 */
export const getListenerCountByType = (
  listenerType: ListenerType | null = null,
): number | Record<string, number> => {
  if (listenerType) {
    // Return count for specific type
    let count = 0;
    activeListeners.forEach((unsubscribe, id) => {
      const parsed = parseListenerId(id);
      if (parsed && parsed.type === listenerType) {
        count++;
      }
    });
    return count;
  }

  // Return counts for all types
  const counts: Record<string, number> = {};
  activeListeners.forEach((unsubscribe, id) => {
    const parsed = parseListenerId(id);
    const type = parsed ? parsed.type : 'unknown';
    counts[type] = (counts[type] || 0) + 1;
  });
  return counts;
};

/**
 * Get current listener count for debugging/verification
 * @returns {number} Number of active listeners
 */
export const getActiveListenerCount = () => {
  return activeListeners.size;
};

/**
 * Get list of active listener IDs for debugging/verification
 * @returns {string[]} Array of active listener IDs
 */
export const getActiveListenerIds = () => {
  return Array.from(activeListeners.keys());
};

/**
 * Debug function to log current listener status with type breakdown
 */
export const logListenerStatus = () => {
  const totalCount = activeListeners.size;
  const listenerIds = getActiveListenerIds();
  const countsByType = getListenerCountByType();

  debugLog(`[FirestoreListeners] Active listeners (${totalCount}):`, listenerIds);

  // Show breakdown by type if we have multiple types
  if (Object.keys(countsByType).length > 1) {
    debugLog(`[FirestoreListeners] Breakdown by type:`, countsByType);
  }
};
